<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import InboxesAPI from 'dashboard/api/inboxes';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const isRunning = ref(false);
// The result is not part of the inbox payload, it has its own admin-only endpoint.
const check = ref(null);
const isLoaded = ref(false);

const STATUS_STYLES = {
  ok: { dot: 'bg-n-teal-9', text: 'text-n-teal-11' },
  warning: { dot: 'bg-n-amber-9', text: 'text-n-amber-11' },
  error: { dot: 'bg-n-ruby-9', text: 'text-n-ruby-11' },
  unknown: { dot: 'bg-n-slate-9', text: 'text-n-slate-11' },
};

const domain = computed(
  () => check.value?.domain || props.inbox.email?.split('@').pop() || ''
);
const status = computed(() =>
  STATUS_STYLES[check.value?.status] ? check.value.status : 'unknown'
);
const issues = computed(() => check.value?.issues || []);
const checkedAt = computed(() => {
  if (!check.value?.checked_at) return null;
  const unix = Math.floor(new Date(check.value.checked_at).getTime() / 1000);
  return dynamicTime(unix);
});
const senderText = computed(() => {
  const sender = check.value?.sender;
  if (!sender) return null;
  if (sender.kind === 'platform') {
    return t('INBOX_MGMT.EMAIL_DNS_CHECK.SENDER.PLATFORM');
  }
  return t('INBOX_MGMT.EMAIL_DNS_CHECK.SENDER.SMTP', {
    host: sender.smtp_host,
  });
});

const issueText = (issue, key) =>
  t(
    `INBOX_MGMT.EMAIL_DNS_CHECK.ISSUES.${issue.code}.${key}`,
    issue.params || {}
  );

const runCheck = async () => {
  isRunning.value = true;
  try {
    const { data } = await InboxesAPI.checkEmailDns(props.inbox.id);
    check.value = data.dns_check || null;
    isLoaded.value = true;
    useAlert(t('INBOX_MGMT.EMAIL_DNS_CHECK.RUN_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.EMAIL_DNS_CHECK.RUN_ERROR'));
  } finally {
    isRunning.value = false;
  }
};

watch(
  () => props.inbox.id,
  async inboxId => {
    check.value = null;
    isLoaded.value = false;
    try {
      const { data } = await InboxesAPI.getEmailDnsCheck(inboxId);
      if (inboxId !== props.inbox.id) return;
      check.value = data.dns_check || null;
      isLoaded.value = true;
    } catch (error) {
      // the section stays without a status, the check can still be run by hand
    }
  },
  { immediate: true }
);
</script>

<template>
  <SettingsSection
    :title="$t('INBOX_MGMT.EMAIL_DNS_CHECK.TITLE')"
    :sub-title="$t('INBOX_MGMT.EMAIL_DNS_CHECK.SUB_TITLE', { domain })"
  >
    <div class="flex flex-col gap-4" data-test-id="email-dns-check">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <div
            v-if="check"
            class="flex items-center gap-2 text-sm font-medium"
            :class="STATUS_STYLES[status].text"
          >
            <span
              class="inline-block rounded-full size-2.5"
              :class="STATUS_STYLES[status].dot"
            />
            {{
              $t(`INBOX_MGMT.EMAIL_DNS_CHECK.STATUS.${status.toUpperCase()}`)
            }}
          </div>
          <p v-else-if="isLoaded" class="mb-0 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.EMAIL_DNS_CHECK.NOT_CHECKED') }}
          </p>
          <p
            v-if="checkedAt || senderText"
            class="flex flex-wrap mb-0 text-xs gap-x-2 text-n-slate-11"
          >
            <span v-if="checkedAt">
              {{
                $t('INBOX_MGMT.EMAIL_DNS_CHECK.CHECKED_AT', {
                  time: checkedAt,
                })
              }}
            </span>
            {{ senderText }}
          </p>
        </div>
        <NextButton
          slate
          outline
          sm
          type="button"
          icon="i-lucide-refresh-cw"
          :is-loading="isRunning"
          :label="$t('INBOX_MGMT.EMAIL_DNS_CHECK.RUN')"
          @click="runCheck"
        />
      </div>

      <p
        v-if="check?.error === 'dns_unavailable'"
        class="mb-0 text-sm text-n-amber-11"
      >
        {{ $t('INBOX_MGMT.EMAIL_DNS_CHECK.DNS_UNAVAILABLE') }}
      </p>

      <div
        v-for="issue in issues"
        :key="issue.code"
        class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
      >
        <div class="flex items-start gap-2">
          <span
            class="px-1.5 py-0.5 text-xs font-medium rounded shrink-0"
            :class="
              issue.severity === 'error'
                ? 'bg-n-ruby-3 text-n-ruby-11'
                : 'bg-n-amber-3 text-n-amber-11'
            "
          >
            {{
              $t(
                `INBOX_MGMT.EMAIL_DNS_CHECK.SEVERITY.${issue.severity.toUpperCase()}`
              )
            }}
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ issueText(issue, 'TITLE') }}
          </span>
        </div>
        <p class="mb-0 text-sm break-words text-n-slate-11">
          {{ issueText(issue, 'DESCRIPTION') }}
        </p>
        <div v-if="issue.fix" class="flex flex-col gap-1 text-sm">
          <span class="text-n-slate-11">
            {{ $t('INBOX_MGMT.EMAIL_DNS_CHECK.FIX') }}
          </span>
          <div
            class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 items-center"
          >
            <span class="text-n-slate-11">
              {{ $t('INBOX_MGMT.EMAIL_DNS_CHECK.RECORD_TYPE') }}
            </span>
            <code class="text-n-slate-12">{{ issue.fix.type }}</code>
            <span class="text-n-slate-11">
              {{ $t('INBOX_MGMT.EMAIL_DNS_CHECK.RECORD_HOST') }}
            </span>
            <code class="break-all text-n-slate-12">{{ issue.fix.host }}</code>
            <span class="self-start pt-2 text-n-slate-11">
              {{ $t('INBOX_MGMT.EMAIL_DNS_CHECK.RECORD_VALUE') }}
            </span>
            <div
              class="min-w-0 [&_pre]:whitespace-pre-wrap [&_pre]:break-all [&_pre]:pr-16"
            >
              <woot-code lang="html" :script="issue.fix.value" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </SettingsSection>
</template>
